<template>
  <div class="agent-avatar" :style="containerStyle">
    <img
      v-if="avatarUrl"
      :src="avatarUrl"
      :alt="avatarKey"
      class="avatar-img"
    />
    <div v-else class="avatar-fallback">{{ avatarKey }}</div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

export const AVATAR_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8"] as const;

export type AvatarKey = (typeof AVATAR_KEYS)[number];

export const AVATAR_NAMES: Record<AvatarKey, string> = {
  "1": "1",
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "7": "7",
  "8": "8",
};

const loadAvatarUrl = (key: AvatarKey): string => {
  try {
    return new URL(`./avatars/${key}.png`, import.meta.url).href;
  } catch {
    return "";
  }
};

const AVATAR_URLS = {
  "1": loadAvatarUrl("1"),
  "2": loadAvatarUrl("2"),
  "3": loadAvatarUrl("3"),
  "4": loadAvatarUrl("4"),
  "5": loadAvatarUrl("5"),
  "6": loadAvatarUrl("6"),
  "7": loadAvatarUrl("7"),
  "8": loadAvatarUrl("8"),
} satisfies Record<AvatarKey, string>;

export function getAvatarUrlByKey(key: string): string {
  const avatarKey = AVATAR_KEYS.find((candidate) => candidate === key);
  return avatarKey ? AVATAR_URLS[avatarKey] : "";
}

export default defineComponent({
  name: "AgentAvatar",
  props: {
    avatarKey: {
      type: String,
      default: "1",
    },
    size: {
      type: Number,
      default: 80,
    },
  },
  setup(props) {
    const avatarUrl = computed(() => getAvatarUrlByKey(props.avatarKey));

    const containerStyle = computed(() => ({
      width: `${props.size}px`,
      height: `${props.size}px`,
    }));

    return { avatarUrl, containerStyle };
  },
});
</script>

<style scoped lang="scss">
.agent-avatar {
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: block;
}

.avatar-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
